import { PolicyDefinition } from "./policyTypes";

export const polHealthSafety: PolicyDefinition[] = [
  {
    id: "hs-fire",
    code: "HS-001",
    title: "Fire Safety and Evacuation",
    category: "health-safety",
    cqcFocus: "Safe",
    sop: true,
    version: "4.0",
    reviewMonths: 12,
    summary: "Prevents fire, keeps escape routes safe, and ensures every resident has a personal evacuation plan and every drill is recorded and learned from.",
    keyPoints: [
      "Fire alarm is tested weekly and recorded.",
      "Escape routes and fire doors are never obstructed or propped.",
      "Every resident has a PEEP reviewed on admission and on change.",
      "Drills run each quarter across all shifts.",
    ],
    relatedRegulations: [
      "Regulatory Reform (Fire Safety) Order 2005",
      "Regulation 12 Safe Care and Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Test the fire alarm weekly and record the result.",
        "2. Check escape routes and exits daily during walkarounds.",
        "3. Keep fire doors closed; never prop them open.",
        "4. Complete a PEEP for every resident on admission and review on any change.",
        "5. Train all staff in fire safety at induction and annually.",
        "6. On alarm activation, follow the evacuation plan to the assembly point.",
        "7. Complete a headcount and building check.",
        "8. Record every activation, false alarm and fault, and report faults the same day.",
        "9. Run a fire drill each quarter covering every shift pattern.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Named fire marshals per shift.",
        "Registered Manager reviews drills and faults monthly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Drill and test log audited monthly.",
        "Fault tracker kept until closure.",
      ]},
    ],
  },
  {
    id: "hs-ipc",
    code: "HS-002",
    title: "Infection Prevention and Control",
    category: "health-safety",
    cqcFocus: "Safe",
    sop: true,
    version: "4.0",
    reviewMonths: 12,
    summary: "Stops infection spreading through hand hygiene, correct PPE, audited cleaning, safe waste and a clear outbreak plan.",
    keyPoints: [
      "Hand hygiene before and after every contact.",
      "PPE chosen for the task and disposed of correctly.",
      "Two or more linked cases are treated as an outbreak and reported.",
      "Cleaning schedules are signed and audited.",
    ],
    relatedRegulations: [
      "Health and Social Care Act 2008 Code of Practice on infection control",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Clean hands before and after every resident contact.",
        "2. Wear PPE for the task: gloves, apron, mask as required.",
        "3. Complete cleaning schedules and sign them; supervisor audits weekly.",
        "4. Segregate waste into the correct streams and store securely.",
        "5. Report two or more linked cases to the manager the same day.",
        "6. Activate the outbreak plan: isolate, cohort, notify UKHSA where required.",
        "7. Handle laundry by category; infected linen bagged at source.",
        "8. Complete IPC training annually.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Named IPC lead on site.",
        "All staff own hand hygiene and clean as you go.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly IPC audit with score and action plan.",
        "Quarterly hand hygiene observation audit.",
      ]},
    ],
  },
  {
    id: "hs-risk",
    code: "HS-003",
    title: "Risk Assessment and Risk Register",
    category: "health-safety",
    cqcFocus: "Well led",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures premises, activity and personal risks are assessed, recorded, controlled and reviewed, with positive risk taking for resident choice.",
    keyPoints: [
      "Every premises and activity risk is assessed and recorded.",
      "Personal risk assessments involve the resident and family.",
      "Reviews happen after incidents and changes, not only annually.",
      "Positive risk taking records benefits and controls.",
    ],
    relatedRegulations: [
      "Management of Health and Safety at Work Regulations 1999",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Identify hazards for premises, activities and each resident.",
        "2. Score likelihood and severity and decide the controls.",
        "3. Record significant findings on the register.",
        "4. Put controls in place and tell the people affected.",
        "5. Review annually, after any incident and when things change.",
        "6. Involve residents and family in personal risk decisions.",
        "7. Record positive risk taking with benefits and controls agreed.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Managers complete premises assessments.",
        "Care staff contribute to personal risk assessments.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Risk register reviewed quarterly with the manager.",
        "Actions tracked to closure.",
      ]},
    ],
  },
  {
    id: "hs-water",
    code: "HS-004",
    title: "Water Safety and Legionella Control",
    category: "health-safety",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Controls legionella and water quality risks through temperature monitoring, flushing, cleaning and complete records.",
    keyPoints: [
      "Hot water 50 C or above and cold water 20 C or below at sentinel points.",
      "Infrequently used outlets are flushed weekly.",
      "Shower heads are cleaned and descaled monthly.",
      "Out of range results are reported and retested the same day.",
    ],
    relatedRegulations: [
      "HSE HSG274 and ACOP L8",
      "Regulation 12 Safe Care and Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Record sentinel tap temperatures weekly.",
        "2. Keep the hot water cylinder at 60 C.",
        "3. Flush infrequently used outlets weekly and record.",
        "4. Clean and descale shower heads monthly.",
        "5. Report out of range results the same day and retest.",
        "6. Review the legionella risk assessment every year.",
        "7. Keep all water safety records for at least two years.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Named water safety lead.",
        "Maintenance provider completes statutory inspections.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Temperature trends reviewed monthly.",
        "Flushing and cleaning logs checked weekly.",
      ]},
    ],
  },
  {
    id: "hs-handling",
    code: "HS-005",
    title: "Moving and Handling and Equipment Safety",
    category: "health-safety",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Protects residents and staff through personal handling plans, trained staff and equipment that is checked, tested and defect-free.",
    keyPoints: [
      "Every resident has a personal handling plan.",
      "Staff are trained and refreshed annually.",
      "Hoists receive LOLER examination every six months.",
      "Defective equipment is quarantined and reported.",
    ],
    relatedRegulations: [
      "Manual Handling Operations Regulations 1992",
      "LOLER 1998 and PUWER 1998",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete a personal handling plan on admission and on any change.",
        "2. Train all handling staff and refresh annually.",
        "3. Carry out pre-use checks on equipment each shift.",
        "4. Book LOLER thorough examination of hoists every six months.",
        "5. Quarantine and label defective equipment; report the same day.",
        "6. Use two carers and the specified sling where the plan says so.",
        "7. Review handling plans after any fall or change in ability.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Staff follow plans exactly; no unsafe lifts.",
        "Manager maintains the equipment register.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Equipment register and test dates checked monthly.",
        "Handling plan audit twice a year.",
      ]},
    ],
  },
  {
    id: "hs-coshh",
    code: "HS-006",
    title: "COSHH and Hazardous Substances",
    category: "health-safety",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Keeps people safe from hazardous substances through a live register, locked storage, training and correct disposal.",
    keyPoints: [
      "A COSHH register lists every substance on site.",
      "Safety data sheets are kept with the register.",
      "Chemicals are locked away and never decanted into unlabelled containers.",
      "Only trained staff handle hazardous substances.",
    ],
    relatedRegulations: [
      "COSHH Regulations 2002",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Keep the COSHH register up to date.",
        "2. Hold a safety data sheet for every listed substance.",
        "3. Store chemicals locked and away from care areas.",
        "4. Never decant into unlabelled containers.",
        "5. Use PPE exactly as the data sheet requires.",
        "6. Dispose of waste substances through approved contractors.",
        "7. Review the register and storage annually.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Manager owns the register.",
        "Staff must not bring personal cleaning products onto site.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Quarterly register and storage check.",
      ]},
    ],
  },
  {
    id: "hs-food",
    code: "HS-007",
    title: "Food Hygiene and Safety",
    category: "health-safety",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Keeps food safe from delivery to plate through temperature control, date coding, allergen management and trained staff.",
    keyPoints: [
      "Fridges run at 0 to 5 C and are recorded twice daily.",
      "Food is cooked to 75 C or above and probed.",
      "Stock is date coded and rotated first in first out.",
      "Allergen and special diet information is available at every meal.",
    ],
    relatedRegulations: [
      "Food Safety Act 1990",
      "Food Hygiene Regulations",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Check deliveries on arrival and store within 30 minutes.",
        "2. Record fridge and freezer temperatures twice daily.",
        "3. Cook to a core temperature of 75 C or above and probe each batch.",
        "4. Date code all stored food and rotate first in first out.",
        "5. Keep allergen information current and communicate special diets to every shift.",
        "6. Keep kitchen staff trained to food hygiene level 2 or above.",
        "7. Complete a kitchen hygiene audit monthly.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Cook in charge owns daily checks.",
        "Manager reviews records weekly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Temperature records reviewed weekly.",
        "Monthly hygiene audit with action plan.",
      ]},
    ],
  },
];